import { useState, useEffect, useCallback } from "react";
import { useTheme } from "./ThemeContext";
import { onRateLimit } from "./rateLimitEmitter";

/**
 * Full-screen overlay shown when the device hits the global rate limit
 * (60 requests/minute). Blocks all interaction until the countdown expires.
 */
export default function RateLimitOverlay() {
  const { isDarkMode } = useTheme();
  const [seconds, setSeconds] = useState(null);

  useEffect(() => {
    return onRateLimit("global_block", (retryAfter) => {
      setSeconds(retryAfter);
    });
  }, []);

  useEffect(() => {
    if (seconds === null || seconds <= 0) {
      if (seconds === 0) setSeconds(null);
      return;
    }
    const id = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  if (seconds === null) return null;

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const display =
    mins > 0
      ? `${mins}:${secs.toString().padStart(2, "0")}`
      : `${secs}s`;

  return (
    <div
      className={`fixed inset-0 z-[10000] flex flex-col items-center justify-center px-6 ${
        isDarkMode ? "bg-gray-950" : "bg-white"
      }`}
    >
      {/* Shield icon */}
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-10 w-10 text-red-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286zm0 13.036h.008v.008H12v-.008z"
          />
        </svg>
      </div>

      <h1
        className={`mb-3 text-2xl font-bold ${
          isDarkMode ? "text-gray-100" : "text-gray-900"
        }`}
      >
        Trop de requêtes
      </h1>

      <p
        className={`mb-8 max-w-sm text-center text-sm leading-relaxed ${
          isDarkMode ? "text-gray-400" : "text-gray-600"
        }`}
      >
        Votre appareil a envoyé trop de requêtes.
        Veuillez patienter avant de réutiliser l'application.
      </p>

      {/* Countdown */}
      <div
        className={`rounded-2xl px-10 py-5 ${
          isDarkMode ? "bg-gray-800" : "bg-gray-100"
        }`}
      >
        <span
          className="text-5xl font-extrabold tabular-nums text-red-500"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {display}
        </span>
      </div>
    </div>
  );
}
